import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Stack, Message, Steps, Tag, Whisper, Tooltip } from "rsuite";
import { io } from "../../../utilities/io";

type Stage = "init" | "clientes" | "contas" | "done";

type ProgressPayload = {
    percent?: number;
    total?: number;
    current?: number;
    done?: number;
    stage?: Stage;
    message?: string;
};

type SocketProgressModalProps = {
    open: boolean;
    onClose: () => void;

    event?: string;
    doneEvent?: string;
    errorEvent?: string;

    title?: string;
    autoCloseOn100?: boolean;
    resetOnOpen?: boolean;
};

function clamp01To100(n: number) {
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, n));
}

function normalizePercent(p: ProgressPayload | number): number {
    if (typeof p === "number") return clamp01To100(p);
    if (typeof p.percent === "number") return clamp01To100(p.percent);

    const current = typeof p.current === "number" ? p.current : p.done;
    if (typeof current === "number" && typeof p.total === "number" && p.total > 0) {
        return clamp01To100((current / p.total) * 100);
    }
    return 0;
}

const COLORS = {
    track: "#e5e7eb",
    active: "#2563eb",
    success: "#16a34a",
    fail: "#ef4444",
    textMuted: "#6b7280",
};

function formatDuration(ms: number) {
    if (!Number.isFinite(ms) || ms <= 0) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m < 60) return `${m}m ${r}s`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
}

function CircularProgress({
    size = 140,
    stroke = 12,
    percent,
    status,
    label,
}: {
    size?: number;
    stroke?: number;
    percent: number;
    status: "active" | "success" | "fail";
    label?: string;
}) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const p = Math.max(0, Math.min(percent, 100));
    const dash = (p / 100) * c;
    const color = status === "success" ? COLORS.success : status === "fail" ? COLORS.fail : COLORS.active;

    return (
        <div style={{ position: "relative", width: size, height: size }}>
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                role="img"
                aria-label={label ?? `Progresso: ${Math.round(percent)}%`}
            >
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke={COLORS.track}
                    strokeWidth={stroke}
                    fill="none"
                />
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        fill="none"
                        strokeDasharray={`${dash} ${c - dash}`}
                    />
                </g>
            </svg>

            {/* Número central */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 24,
                    fontVariantNumeric: "tabular-nums",
                }}
                aria-live="polite"
            >
                {Math.round(percent)}%
            </div>
        </div>
    );
}

const STAGES: Stage[] = ["init", "clientes", "contas", "done"];
const STAGE_LABEL: Record<Stage, string> = {
    init: "Preparando",
    clientes: "Clientes",
    contas: "Contas",
    done: "Concluído",
};

const SocketProgressModal: React.FC<SocketProgressModalProps> = ({
    open,
    onClose,
    event = "import:progress",
    doneEvent = "import:done",
    errorEvent = "import:error",
    title = "Processando...",
    autoCloseOn100 = true,
    resetOnOpen = true,
}) => {
    const [percent, setPercent] = useState(0);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [stage, setStage] = useState<Stage>("init");
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [current, setCurrent] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState<number | undefined>(undefined);

    const startedAtRef = useRef<number | null>(null);
    const lastTickRef = useRef<{ t: number; value: number } | null>(null);
    const hasResetRef = useRef<boolean>(false);
    const [etaMs, setEtaMs] = useState<number | null>(null);
    const [rate, setRate] = useState<number | null>(null); // items/second

    useEffect(() => {
        if (!open) {
            hasResetRef.current = false;
            return;
        }

        if (resetOnOpen && !hasResetRef.current) {
            setPercent(0);
            setErrorMsg(null);
            setStage("init");
            setMessage(undefined);
            setCurrent(undefined);
            setTotal(undefined);
            setEtaMs(null);
            setRate(null);
            startedAtRef.current = Date.now();
            lastTickRef.current = null;
            hasResetRef.current = true;
        }

        const handleProgress = (payload: ProgressPayload) => {
            const next = normalizePercent(payload);
            setPercent(next);

            if (payload.stage) setStage(payload.stage);
            if (payload.message) setMessage(payload.message);
            if (typeof payload.current === "number" || typeof payload.done === "number")
                setCurrent((payload.current ?? payload.done) as number);
            if (typeof payload.total === "number") setTotal(payload.total);

            // ETA & rate (se tivermos current/total)
            const t = Date.now();
            const itemsDone = (payload.current ?? payload.done) ?? null;
            if (itemsDone !== null && typeof payload.total === "number" && startedAtRef.current) {
                // taxa média desde o início
                const elapsed = (t - startedAtRef.current) / 1000;
                const avgRate = itemsDone > 0 && elapsed > 0 ? itemsDone / elapsed : null;
                setRate(avgRate);

                const remaining = Math.max(0, payload.total - itemsDone);
                const eta = avgRate && avgRate > 0 ? (remaining / avgRate) * 1000 : null;
                setEtaMs(eta);
            }

            if ((payload?.stage === "done" || next >= 100) && autoCloseOn100) {
                setTimeout(onClose, 600);
            }
        };

        const handleDone = (_payload?: { message?: string }) => {
            setPercent(100);
            setStage("done");
            if (_payload?.message) setMessage(_payload.message);
            if (autoCloseOn100) setTimeout(onClose, 600);
        };

        const handleError = (payload?: { message?: string }) => {
            setErrorMsg(payload?.message ?? "Ocorreu um erro no processamento.");
        };

        io.on(event, handleProgress);
        io.on(doneEvent, handleDone);
        io.on(errorEvent, handleError);

        return () => {
            io.off(event, handleProgress);
            io.off(doneEvent, handleDone);
            io.off(errorEvent, handleError);
        };
    }, [open, event, doneEvent, errorEvent, autoCloseOn100, resetOnOpen, onClose]);

    const status: "active" | "success" | "fail" =
        errorMsg ? "fail" : percent >= 100 ? "success" : "active";

    const stageIndex = useMemo(() => Math.max(0, STAGES.indexOf(stage)), [stage]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="md"
            keyboard={status !== "active" || !!errorMsg}
            overflow={true}
            backdrop="static"
        >
            {/* <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header> */}

            <Modal.Body style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Stack spacing={16} alignItems="center" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Coluna: indicador circular */}
                    <Stack direction="column" spacing={8} style={{ width: '100%' }}>
                        <CircularProgress
                            percent={percent}
                            status={status}
                            label={`Progresso: ${Math.round(percent)}%`}
                        />
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                            {status === "active" && "Em andamento"}
                            {status === "success" && "Concluído"}
                            {status === "fail" && "Falhou"}
                        </div>
                        {typeof current === "number" && typeof total === "number" && (
                            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                                {current} / {total}
                                {rate ? ` • ${rate.toFixed(1)} it/s` : ""}
                                {etaMs !== null ? ` • ETA ${formatDuration(etaMs)}` : ""}
                            </div>
                        )}
                    </Stack>

                    {/* Coluna: detalhes/etapas */}
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <Stack spacing={8} alignItems="center" style={{ marginBottom: 6 }}>
                            <Tag size="sm" color={status === "fail" ? "red" : status === "success" ? "green" : "blue"}>
                                {STAGE_LABEL[stage]}
                            </Tag>
                            {message && (
                                <Whisper placement="top" trigger="hover" speaker={<Tooltip>{message}</Tooltip>}>
                                    <span style={{ fontSize: 12, color: COLORS.textMuted, cursor: "help" }}>
                                        ver mensagem
                                    </span>
                                </Whisper>
                            )}
                        </Stack>

                        <Steps current={stageIndex} vertical small>
                            <Steps.Item title="Preparando" description="Lendo arquivo e verificando dados" />
                            <Steps.Item title="Clientes" description="Criando/atualizando clientes" />
                            <Steps.Item title="Contas" description="Registrando contas" />
                            <Steps.Item title="Concluído" description="Finalizando e limpando" />
                        </Steps>

                        {message && (
                            <div style={{ marginTop: 8, fontSize: 13 }}>
                                <strong>Detalhes:</strong> {message}
                            </div>
                        )}

                        {errorMsg && (
                            <Message type="error" showIcon style={{ marginTop: 12 }}>
                                {errorMsg}
                            </Message>
                        )}
                    </div>
                </Stack>
            </Modal.Body>

            <Modal.Footer>
                <Stack justifyContent="flex-end" spacing={8}>
                    {/* {status === "active" && !errorMsg && (
                        <Button appearance="subtle" onClick={onClose}>
                            Ocultar
                        </Button>
                    )} */}
                    {(status === "success" || !!errorMsg) && (
                        <Button appearance="primary" onClick={onClose}>
                            Fechar
                        </Button>
                    )}
                </Stack>
            </Modal.Footer>
        </Modal>
    );
};

export default SocketProgressModal;
